import { motion } from "framer-motion"
import { useCallback, useEffect, useMemo, useState } from "react"
import type { ReactNode } from "react"
import { useSearchParams } from "react-router-dom"
import { Table } from "../../components/Table"
import { SidePane } from "../../components/SidePane"
import { Tabs } from "../../components/Tabs"
import { QR } from "../../components/QR"
import { useThemeSurfaces } from "../../theme/useThemeSurfaces"
import { useControlCenter } from "../../AppShell"
import {
  useContacts,
  useContactLogs,
  useContactMessages,
  useContactNotes,
  useIssueBalanceNow,
  useRefreshContext,
  useResendPayLink,
  useSaveContactNote,
  useSendContactMessage,
  useStatusHealth,
} from "../../hooks/useApi"
import { ChatComposer } from "../../components/chat/Composer"
import { MessageList } from "../../components/chat/MessageList"
import { rememberLastContact } from "../../constants/storage"
import type {
  Contact,
  ContactMessage,
  ContactLogs,
  MessageChannel,
  OpsReasoningEntry,
  IntentConfidence,
} from "../../types"
import { ApiError } from "../../api/http"
import { GhostNoteForm } from "../../components/chat/GhostNoteForm"
import { DegradedNotice } from "../../components/DegradedNotice"
import {
  buildGhostNoteIndex,
  createGhostNoteContextCards,
  formatConfidenceScore,
  type GhostNoteContextCard,
} from "../../utils/ghostNotes"
import { Tooltip } from "../../components/Tooltip"
import { trackDegradedRails } from "../../utils/telemetry"
import { ConfidenceMeter } from "../../components/contact/ConfidenceMeter"
import { StatusBeacon } from "../../components/StatusBeacon"
import { ActionChip } from "../../components/subscription"

const TAB_ITEMS = [
  { id: "chat", label: "Chat log" },
  { id: "ops", label: "Agent log" },
]

export default function DemonsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [query, setQuery] = useState("")
  const [selected, setSelected] = useState<Contact | null>(null)
  const [activeTab, setActiveTab] = useState<string>("chat")
  const contactsQuery = useContacts({ query })
  const contacts = useMemo(
    () => contactsQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [contactsQuery.data],
  )
  const logs = useContactLogs(selected?.id)
  const notesQuery = useContactNotes(selected?.id)
  const messagesQuery = useContactMessages(selected?.id ?? null)
  const resend = useResendPayLink()
  const issue = useIssueBalanceNow()
  const sendMessage = useSendContactMessage()
  const saveGhostNote = useSaveContactNote()
  const { tenant } = useControlCenter()
  const refresh = useRefreshContext(tenant.id ?? 1)
  const statusHealth = useStatusHealth()
  const statusFallback = useMemo(
    () => ({
      stripe: tenant.stripe_webhook ?? "unknown",
      twilio: tenant.twilio_webhook ?? "unknown",
      notifications: "unknown",
      subscriptions: "unknown",
    }),
    [tenant.stripe_webhook, tenant.twilio_webhook],
  )
  const [messageBody, setMessageBody] = useState("")
  const [channel, setChannel] = useState<MessageChannel>("sms")
  const [composerError, setComposerError] = useState<string | null>(null)
  const [ghostFormMessageId, setGhostFormMessageId] = useState<number | null>(null)
  const [ghostError, setGhostError] = useState<string | null>(null)

  const surfaces = useThemeSurfaces()
  const panelTokens = surfaces.panel
  const buttonTokens = surfaces.button
  const inputTokens = surfaces.input
  const cardPanel = panelTokens.primary
  const insetPanel = panelTokens.inset
  const tertiaryPanel = panelTokens.tertiary
  const secondaryPanel = panelTokens.secondary
  const outlineButton = buttonTokens.outline
  const headingColor = cardPanel.heading

  const recordLastContact = useCallback((contactId: string) => {
    rememberLastContact(contactId)
  }, [])

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === "/" && !event.metaKey && !event.ctrlKey) {
        event.preventDefault()
        const input = document.getElementById("demons-search") as HTMLInputElement | null
        input?.focus()
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [])

  useEffect(() => {
    const contactId = searchParams.get("contactId")
    if (!contactId) {
      setSelected(null)
      return
    }
    const match = contacts.find((contact) => contact.id === contactId)
    if (match) {
      setSelected((current) => current?.id === contactId ? current : match)
      setActiveTab("chat")
      recordLastContact(match.id)
    }
  }, [searchParams, contacts, recordLastContact])

  useEffect(() => {
    setMessageBody("")
    setComposerError(null)
    setChannel("sms")
    setGhostFormMessageId(null)
    setGhostError(null)
  }, [selected?.id])

  const amountFormatter = useMemo(() => new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }), [])

  const columns = [
    {
      key: "customer",
      title: "CUSTOMER",
      render: (row: Contact) => row.name,
    },
    {
      key: "status",
      title: "STATUS",
      render: (row: Contact) => <StatusPill status={row.status} />,
    },
    {
      key: "chat_count",
      title: "CHAT LOG",
      render: (row: Contact) => `${row.chat_count} entries`,
    },
    {
      key: "balance_cents",
      title: "BALANCE",
      align: "right" as const,
      render: (row: Contact) => amountFormatter.format(row.balance_cents / 100),
    },
  ]

  const { data: contactLogs } = logs
  const messages = useMemo(() => messagesQuery.data ?? [], [messagesQuery.data])
  const latestConfidence = useMemo(() => findLatestIntentConfidence(messages), [messages])
  const composerDisabled = !tenant.allow_web_send || !selected
  const ghostNotes = useMemo(() => notesQuery.data?.ghost_notes ?? [], [notesQuery.data])
  const ghostNoteIndex = useMemo(() => buildGhostNoteIndex(ghostNotes), [ghostNotes])
  const ghostCards = useMemo(() => createGhostNoteContextCards(ghostNotes), [ghostNotes])
  const degradedBanner = statusHealth.data?.degraded_banner
  const degradedRails = useMemo(() => degradedBanner?.rails ?? [], [degradedBanner?.rails])
  useEffect(() => {
    if (degradedBanner?.state === "degraded" && degradedRails.length) {
      trackDegradedRails(degradedRails, "demons.drawer")
    }
  }, [degradedBanner?.state, degradedRails])
  const activeGhostMessage = useMemo(
    () => (ghostFormMessageId ? messages.find((message) => message.id === ghostFormMessageId) ?? null : null),
    [messages, ghostFormMessageId],
  )
  const activeGhostNote = useMemo(() => {
    if (!ghostFormMessageId) return null
    const indexed = ghostNoteIndex[ghostFormMessageId]
    if (indexed) return indexed
    const message = messages.find((entry) => entry.id === ghostFormMessageId)
    if (!message?.ghost_note) return null
    const raw = message.ghost_note as Record<string, unknown>
    const summary = typeof raw["summary"] === "string" ? (raw["summary"] as string) : message.body.slice(0, 80)
    return {
      message_id: message.id,
      summary,
      ghost_note: message.ghost_note,
      confidence_label: message.confidence_label ?? null,
      confidence_score: message.confidence_score ?? null,
      confidence_sources: Array.isArray(message.confidence_sources) ? message.confidence_sources : [],
      created_at: message.created_at,
      updated_at: message.created_at,
    }
  }, [ghostFormMessageId, ghostNoteIndex, messages])

  const handleSelectContact = useCallback(
    (contact: Contact) => {
      setActiveTab("chat")
      const next = new URLSearchParams(searchParams)
      next.set("contactId", contact.id)
      setSearchParams(next, { replace: true })
      recordLastContact(contact.id)
    },
    [recordLastContact, searchParams, setSearchParams],
  )

  const handleClosePane = () => {
    const next = new URLSearchParams(searchParams)
    next.delete("contactId")
    setSearchParams(next, { replace: true })
  }

  const handleSend = useCallback(async () => {
    if (!selected || !messageBody.trim()) return
    setComposerError(null)
    try {
      await sendMessage.mutateAsync({
        contactId: selected.id,
        body: messageBody.trim(),
        channel,
      })
      setMessageBody("")
    } catch (error) {
      if (error instanceof ApiError) {
        setComposerError(error.body && typeof error.body === "object" ? "Unable to send message." : error.message)
      } else {
        setComposerError("Unable to send message. Try again soon.")
      }
    }
  }, [selected, messageBody, channel, sendMessage])

  const handleOpenGhostForm = useCallback((messageId: number) => {
    setGhostFormMessageId(messageId)
    setGhostError(null)
  }, [])

  const handleGhostCancel = useCallback(() => {
    setGhostFormMessageId(null)
    setGhostError(null)
  }, [])

  const handleGhostSubmit = useCallback(
    (payload: {
      summary: string
      confidence_label?: string | null
      confidence_score?: number | null
      sources?: string[]
      details?: Record<string, unknown>
    }) => {
      if (!selected || !ghostFormMessageId) return
      setGhostError(null)
      saveGhostNote.mutate(
        {
          contactId: selected.id,
          payload: {
            message_id: ghostFormMessageId,
            ...payload,
          },
        },
        {
          onSuccess: () => {
            setGhostFormMessageId(null)
          },
          onError: (error) => {
            if (error instanceof ApiError) {
              setGhostError(error.message)
            } else {
              setGhostError("Unable to save note. Try again soon.")
            }
          },
        },
      )
    },
    [saveGhostNote, selected, ghostFormMessageId],
  )

  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "clamp(16px, 3vw, 24px)" }}>
      <input
        id="demons-search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="/ search customers"
        className="mono"
        style={{
          padding: "12px 18px",
          border: inputTokens.border,
          background: inputTokens.background,
          color: inputTokens.color,
          letterSpacing: "0.12em",
          width: "min(100%, 520px)",
        }}
      />
      <Table
        data={contacts}
        columns={columns}
        getRowId={(row) => row.id}
        onRowClick={handleSelectContact}
        activeRowId={selected?.id ?? null}
        enableKeyboardNav
        ariaLabel="Customers"
      />
      {contactsQuery.isFetching ? <div className="small">Loading customers…</div> : null}
      {contactsQuery.hasNextPage ? (
        <motion.button
          type="button"
          whileTap={{ scale: 0.95 }}
          onClick={() => contactsQuery.fetchNextPage()}
          disabled={contactsQuery.isFetchingNextPage}
          className="mono"
          style={{
            alignSelf: "flex-start",
            padding: "8px 14px",
            border: outlineButton.border,
            background: outlineButton.background,
            color: outlineButton.color,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            cursor: contactsQuery.isFetchingNextPage ? "progress" : "pointer",
            minHeight: "44px",
            boxShadow: outlineButton.shadow,
          }}
        >
          {contactsQuery.isFetchingNextPage ? "Loading…" : "Load more"}
        </motion.button>
      ) : null}

      <section
        style={{
          border: tertiaryPanel.border,
          background: tertiaryPanel.background,
          boxShadow: tertiaryPanel.shadow,
          padding: "16px",
          display: "grid",
          gap: "12px",
        }}
      >
        <header className="mono" style={{ letterSpacing: "0.14em", textTransform: "uppercase", color: headingColor }}>
          Service status
        </header>
        <StatusBeacon
          health={statusHealth.data}
          loading={statusHealth.isLoading}
          fallback={statusFallback}
          banner={statusHealth.data?.degraded_banner}
        />
      </section>

      <SidePane
        paneKey={selected ? `contact-${selected.id}` : undefined}
        isOpen={Boolean(selected)}
        onClose={handleClosePane}
        title="Customer details"
        actions={
          selected ? (
            <div className="detail-actions detail-actions--compact">
              <ActionChip
                label="Resend pay link"
                loading={resend.isPending}
                disabled={!selected.latest_invoice_id}
                onClick={() =>
                  selected &&
                  selected.latest_invoice_id &&
                  resend.mutate({
                    invoiceId: selected.latest_invoice_id,
                    contactId: selected.id,
                    tenantId: tenant.id ?? 1,
                  })
                }
              />
              <ActionChip
                label="Send balance invoice"
                loading={issue.isPending}
                disabled={!selected.latest_invoice_id}
                onClick={() =>
                  selected &&
                  selected.latest_invoice_id &&
                  issue.mutate({
                    invoiceId: selected.latest_invoice_id,
                    contactId: selected.id,
                    tenantId: tenant.id ?? 1,
                  })
                }
              />
              <ActionChip
                label="Refresh"
                loading={refresh.isPending}
                onClick={() => selected && refresh.mutate(selected.id)}
              />
            </div>
          ) : null
        }
      >
        {selected ? (
          <div className="detail-pane" key={selected.id}>
            <section className="detail-pane__hero">
              <div className="detail-pane__heading">
                <span className="detail-pane__eyebrow">Customer</span>
                <h2 className="detail-pane__title">{selected.name}</h2>
                <div className="detail-pane__meta">
                  {selected.last_message_at ? <span>Last reply {formatDate(selected.last_message_at)}</span> : null}
                </div>
              </div>
              <div className="detail-pane__badge">
                <span className={`detail-pane__status detail-pane__status--${selected.status}`}>
                  {selected.status.toUpperCase()}
                </span>
                <span className="detail-pane__amount">{amountFormatter.format(selected.balance_cents / 100)}</span>
              </div>
            </section>

            {degradedBanner?.state === "degraded" ? (
              <DegradedNotice banner={degradedBanner} persona={selected.persona === "fairy" ? "fairy" : "demon"} />
            ) : null}

            {!tenant.allow_web_send ? (
              <div
                className="mono small"
                style={{
                  border: "1px dashed var(--rail)",
                  padding: "12px 14px",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                }}
              >
                Sending from the web is disabled — reply by SMS instead.
              </div>
            ) : null}

            <GhostNoteDeck
              loading={notesQuery.isFetching && !ghostCards.length}
              cards={ghostCards}
              onEdit={handleOpenGhostForm}
            />

            <Tabs tabs={TAB_ITEMS} activeId={activeTab} onChange={setActiveTab} />
            {activeTab === "chat" ? (
              <div style={{ display: "grid", gap: "16px" }}>
                {latestConfidence ? <ConfidenceMeter confidence={latestConfidence} /> : null}
                {messagesQuery.isLoading && !messages.length ? (
                  <div className="small">Loading conversation…</div>
                ) : (
                  <MessageList
                    messages={messages}
                    pending={messagesQuery.isFetching}
                    ghostNotes={ghostNoteIndex}
                    onCreateGhostNote={handleOpenGhostForm}
                    onEditGhostNote={handleOpenGhostForm}
                  />
                )}
                {ghostError ? (
                  <div className="mono small" role="alert" style={{ color: "var(--error-text)" }}>
                    {ghostError}
                  </div>
                ) : null}
                {ghostFormMessageId && activeGhostMessage ? (
                  <div
                    style={{
                      border: insetPanel.border,
                      padding: "16px",
                      background: insetPanel.background,
                      display: "grid",
                      gap: "12px",
                      boxShadow: secondaryPanel.shadow,
                    }}
                  >
                    <GhostNoteForm
                      messageId={ghostFormMessageId}
                      messagePreview={activeGhostMessage.body}
                      defaultValue={activeGhostNote}
                      pending={saveGhostNote.isPending}
                      onSubmit={handleGhostSubmit}
                      onCancel={handleGhostCancel}
                    />
                  </div>
                ) : null}
                <ChatComposer
                  disabled={composerDisabled}
                  channel={channel}
                  onChannelChange={setChannel}
                  value={messageBody}
                  onChange={setMessageBody}
                  onSubmit={handleSend}
                  pending={sendMessage.isPending}
                  error={composerError}
                  showDisabledBanner={!tenant.allow_web_send}
                />
              </div>
            ) : activeTab === "ops" ? (
              <OpsView
                logs={contactLogs}
                tone={selected.persona === "fairy" ? "fairy" : "demon"}
              />
            ) : null}
            <QR value={`sms:${tenant.number_e164}?body=Hi%20${encodeURIComponent(selected.name)}`} size={120} />
          </div>
        ) : (
          <div className="small">Select a customer to view their conversation.</div>
        )}
      </SidePane>
    </div>
  )
}

function findLatestIntentConfidence(messages: ContactMessage[]): IntentConfidence | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const metadata = messages[index].metadata
    if (!metadata || typeof metadata !== "object") continue
    const rawConfidence = (metadata as Record<string, unknown>).intent_confidence
    if (!rawConfidence || typeof rawConfidence !== "object") continue
    const confidenceRecord = rawConfidence as Record<string, unknown>
    const rawScore = confidenceRecord.score
    const scoreValue = typeof rawScore === "number" ? rawScore : Number(rawScore)
    const labelValue = confidenceRecord.label
    if (!Number.isFinite(scoreValue) || typeof labelValue !== "string") {
      continue
    }
    const reasons = Array.isArray(confidenceRecord.reasons)
      ? (confidenceRecord.reasons.filter((item) => typeof item === "string") as string[])
      : []
    const sources = Array.isArray(confidenceRecord.sources)
      ? (confidenceRecord.sources.filter((item) => typeof item === "string") as string[])
      : []
    const evaluatedAt = typeof confidenceRecord.evaluated_at === "string" ? confidenceRecord.evaluated_at : undefined
    const requiresConfirm = Boolean(confidenceRecord.requires_confirm)
    const degraded = Boolean(confidenceRecord.degraded)
    return {
      score: Math.max(0, Math.min(1, scoreValue)),
      label: labelValue as IntentConfidence["label"],
      reasons,
      sources,
      evaluated_at: evaluatedAt,
      requires_confirm: requiresConfirm,
      degraded,
      thresholds:
        typeof confidenceRecord.thresholds === "object" && confidenceRecord.thresholds
          ? (confidenceRecord.thresholds as Record<string, number>)
          : undefined,
    }
  }
  return null
}

function GhostNoteDeck({
  cards,
  loading,
  onEdit,
}: {
  cards: GhostNoteContextCard[]
  loading: boolean
  onEdit: (messageId: number) => void
}) {
  const surfaces = useThemeSurfaces()
  const panelTokens = surfaces.panel
  const insetPanel = panelTokens.inset
  const secondaryPanel = panelTokens.secondary
  const mutedColor = panelTokens.primary.muted
  const highlightTokens = surfaces.highlight
  const outlineButton = surfaces.button.outline
  const borderTokens = surfaces.border

  if (loading) {
    return <div className="small">Loading notes…</div>
  }

  if (!cards.length) {
    return (
      <div className="small" style={{ textTransform: "none" }}>
        No private notes yet. Open a message in the chat log to add one.
      </div>
    )
  }

  return (
    <div style={{ display: "grid", gap: "12px" }}>
      <header className="mono" style={{ letterSpacing: "0.14em", textTransform: "uppercase" }}>
        Private notes
      </header>
      <ul style={{ display: "grid", gap: "12px", padding: 0, margin: 0 }}>
        {cards.map((card) => {
          const tooltipParts = [card.confidenceLabel, formatConfidenceScore(card.confidenceScore)]
          if (card.sources.length) {
            tooltipParts.push(card.sources.join(", "))
          }
          return (
            <li key={card.messageId} style={{ listStyle: "none" }}>
              <Tooltip label={tooltipParts.join(" · ")}>
                <motion.div
                  layout
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                  style={{
                    border: insetPanel.border,
                    background: insetPanel.background,
                    padding: "14px 16px",
                    display: "grid",
                    gap: "8px",
                    boxShadow: secondaryPanel.shadow,
                  }}
                >
                  <div className="tiny mono" style={{ color: mutedColor }}>
                    Message #{card.messageId} · {card.confidenceLabel}
                  </div>
                  <div className="mono" style={{ fontSize: "12px", lineHeight: 1.4 }}>{card.summary}</div>
                  <div className="tiny mono" style={{ color: mutedColor }}>
                    Confidence {formatConfidenceScore(card.confidenceScore)}
                  </div>
                  {card.sources.length ? (
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                      {card.sources.map((source) => (
                        <span
                          key={source}
                          className="tiny mono"
                          style={{
                            background: highlightTokens.pill,
                            padding: "2px 6px",
                            letterSpacing: "0.08em",
                          }}
                        >
                          {source}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  <button
                    type="button"
                    className="tiny mono"
                    onClick={() => onEdit(card.messageId)}
                    style={{
                      justifySelf: "flex-start",
                      background: outlineButton.background,
                      border: borderTokens.dashed,
                      padding: "6px 10px",
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      cursor: "pointer",
                      color: outlineButton.color,
                      boxShadow: outlineButton.shadow,
                    }}
                  >
                    Edit note
                  </button>
                </motion.div>
              </Tooltip>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function StatusPill({ status }: { status: Contact["status"] }) {
  const colors: Record<Contact["status"], string> = {
    active: "var(--rail)",
    awaiting: "#60a5fa",
    overdue: "#ff7a1a",
    fairy: "#10b981",
  }
  const labels: Record<Contact["status"], string> = {
    active: "ACTIVE",
    awaiting: "AWAITING",
    overdue: "OVERDUE",
    fairy: "FAIRY",
  }
  return (
    <span className="small" style={{ color: colors[status] }}>
      {labels[status]}
    </span>
  )
}

function OpsView({ logs, tone }: { logs?: ContactLogs; tone: "demon" | "fairy" }) {
  const mutedColor = useThemeSurfaces().panel.primary.muted
  const [view, setView] = useState<"narrative" | "reasoning">("reasoning")
  const reasoning = useMemo<OpsReasoningEntry[]>(() => {
    if (!logs) return []
    return logs.reasoning && logs.reasoning.length ? logs.reasoning : buildReasoningFallback(logs, tone)
  }, [logs, tone])

  if (!logs) {
    return <div className="small">Loading history…</div>
  }

  const narrative = logs.ops ?? []

  return (
    <div style={{ display: "grid", gap: "16px" }}>
      <div style={{ display: "flex", gap: "10px" }}>
        <FilterButton active={view === "reasoning"} onClick={() => setView("reasoning")}>
          Reasoning
        </FilterButton>
        <FilterButton active={view === "narrative"} onClick={() => setView("narrative")}>
          Narrative
        </FilterButton>
      </div>
      {view === "reasoning" ? (
        <ReasoningTimeline entries={reasoning} tone={tone} />
      ) : (
        <div style={{ display: "grid", gap: "12px" }}>
          {narrative.map((entry) => (
            <div key={entry.at + entry.detail} style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <div className="small" style={{ color: mutedColor }}>
                {new Date(entry.at).toLocaleString()}
              </div>
              <div className="mono">{mapToneDetail(entry.detail, tone)}</div>
            </div>
          ))}
          {narrative.length === 0 ? <div className="small">Agent activity will appear here.</div> : null}
        </div>
      )}
    </div>
  )
}

function FilterButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  const { button } = useThemeSurfaces()
  const token = active ? button.outlineStrong : button.outline

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="mono"
      style={{
        padding: "8px 12px",
        border: token.border,
        background: token.background,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        cursor: "pointer",
        color: token.color,
        boxShadow: token.shadow,
      }}
    >
      {children}
    </button>
  )
}

function ReasoningTimeline({ entries, tone }: { entries: OpsReasoningEntry[]; tone: "demon" | "fairy" }) {
  const surfaces = useThemeSurfaces()
  const buttonTokens = surfaces.button
  const accentColor = tone === "fairy" ? surfaces.highlight.pill : buttonTokens.outlineStrong.color
  const mutedColor = surfaces.panel.primary.muted
  const insetPanel = surfaces.panel.inset
  const cardShadow = surfaces.panel.secondary.shadow
  const sourceBackground = surfaces.highlight.soft

  if (!entries.length) {
    return <div className="small">No agent activity captured yet.</div>
  }

  return (
    <div style={{ position: "relative", paddingLeft: "18px", display: "grid", gap: "14px" }}>
      <span
        aria-hidden
        style={{
          position: "absolute",
          left: "6px",
          top: 0,
          bottom: 0,
          width: "2px",
          background: accentColor,
          opacity: 0.4,
        }}
      />
      {entries.map((entry) => (
        <motion.div
          key={`${entry.id ?? entry.at}-${entry.label}`}
          layout
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          style={{
            border: insetPanel.border,
            background: insetPanel.background,
            padding: "12px 14px",
            display: "grid",
            gap: "6px",
            boxShadow: cardShadow,
          }}
        >
          <div className="tiny mono" style={{ color: mutedColor }}>
            {formatTimestamp(entry.at)} · {entry.label}
          </div>
          {entry.summary ? (
            <div className="mono" style={{ fontSize: "12px", lineHeight: 1.4 }}>{entry.summary}</div>
          ) : null}
          {entry.detail ? (
            <div className="tiny mono" style={{ color: mutedColor }}>{entry.detail}</div>
          ) : null}
          <div className="tiny mono" style={{ color: mutedColor }}>
            Confidence {(entry.confidence_label ?? "unknown").toUpperCase()} · {formatConfidenceScore(entry.confidence_score)}
          </div>
          {entry.sources && entry.sources.length ? (
            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
              {entry.sources.map((source) => (
                <span
                  key={source}
                  className="tiny mono"
                  style={{
                    background: sourceBackground,
                    padding: "2px 6px",
                    letterSpacing: "0.08em",
                  }}
                >
                  {source}
                </span>
              ))}
            </div>
          ) : null}
        </motion.div>
      ))}
    </div>
  )
}

function buildReasoningFallback(logs: ContactLogs, tone: "demon" | "fairy"): OpsReasoningEntry[] {
  if (logs.reasoning && logs.reasoning.length) {
    return logs.reasoning
  }

  const primary = logs.ops[0]
  return [
    {
      id: `fallback-${tone}`,
      at: primary?.at ?? new Date().toISOString(),
      label: tone === "fairy" ? "Fairy inference" : "Demon inference",
      summary: primary ? mapToneDetail(primary.detail, tone) : "Waiting for agent activity",
      persona: tone,
      confidence_label: primary ? "medium" : "low",
      confidence_score: primary ? 0.52 : 0.34,
      sources: primary ? [primary.kind] : ["ops-log"],
    },
  ]
}

function mapToneDetail(detail: string, tone: "demon" | "fairy") {
  if (tone === "fairy") {
    return detail.replace(/muttered/gi, "whispered").replace(/etched/gi, "glimmered").replace(/forged/gi, "wove")
  }
  return detail
}

function formatTimestamp(value?: string) {
  if (!value) return "—"
  try {
    return new Date(value).toLocaleString()
  } catch {
    return value
  }
}

function formatDate(value: string) {
  try {
    const date = new Date(value)
    return new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(date)
  } catch {
    return value
  }
}
